/**
 * Homepage Redesign - Scroll Animations & Interactions
 * Luxury Editorial Theme for DapperFam
 */

(function() {
  'use strict';

  // Only run on homepage
  if (!document.body.classList.contains('template-index')) return;

  /**
   * Section Reveal on Scroll
   * Uses Intersection Observer for performant scroll detection
   */
  function initSectionReveals() {
    const revealSections = document.querySelectorAll(
      '.section-reveal, ' +
      '.section-featured-collection, ' +
      '.section-collection-list, ' +
      '.section-gallery, ' +
      '.section-rich-text, ' +
      '.shop-the-look, ' +
      '.section-recently-viewed, ' +
      '.section-editorial-split, ' +
      '.section-social-proof, ' +
      '.section-email-capture, ' +
      '.section-category-quick-nav'
    );

    if (!revealSections.length) return;

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      revealSections.forEach(section => {
        section.classList.add('is-visible');
      });
      return;
    }

    const observerOptions = {
      root: null,
      rootMargin: '0px 0px -10% 0px', // Trigger slightly before element enters viewport
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          // Unobserve after revealing (one-time animation)
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    revealSections.forEach(section => {
      observer.observe(section);
    });
  }

  /**
   * Stagger Product Cards Animation
   * Adds staggered animation delays to product cards within visible sections
   */
  function initProductCardStagger() {
    const productContainers = document.querySelectorAll(
      '.section-featured-collection .product-grid, ' +
      '.section-recently-viewed .product-grid'
    );

    productContainers.forEach(container => {
      const cards = container.querySelectorAll('.product-card');
      cards.forEach((card, index) => {
        card.style.transitionDelay = `${0.1 + (index * 0.05)}s`;
        card.classList.add('stagger-item');
      });
    });
  }

  /**
   * Smooth Scroll for Anchor Links
   */
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        const targetId = this.getAttribute('href');
        if (targetId === '#') return;

        const target = document.querySelector(targetId);
        if (target) {
          e.preventDefault();
          const headerOffset = 80; // Account for sticky header
          const elementPosition = target.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      });
    });
  }

  /**
   * Parallax Effect for Editorial Split Images
   */
  function initParallax() {
    const parallaxElements = document.querySelectorAll('[data-parallax]');
    if (!parallaxElements.length) return;

    // Skip on mobile for performance
    if (window.innerWidth < 768) return;

    // Check for reduced motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let ticking = false;

    function updateParallax() {
      parallaxElements.forEach(element => {
        const rect = element.getBoundingClientRect();
        const scrollPercent = (rect.top + rect.height / 2 - window.innerHeight / 2) / window.innerHeight;
        const translateY = scrollPercent * 30; // Max 30px parallax

        const img = element.querySelector('img');
        if (img) {
          img.style.transform = `translateY(${translateY}px) scale(1.05)`;
        }
      });
      ticking = false;
    }

    window.addEventListener('scroll', function() {
      if (!ticking) {
        requestAnimationFrame(updateParallax);
        ticking = true;
      }
    }, { passive: true });
  }

  /**
   * Image Lazy Load Enhancement
   * Adds fade-in effect when images load
   */
  function initImageFadeIn() {
    const images = document.querySelectorAll('.template-index img[loading="lazy"]');

    images.forEach(img => {
      if (img.complete) {
        img.classList.add('is-loaded');
      } else {
        img.addEventListener('load', function() {
          this.classList.add('is-loaded');
        });
      }
    });
  }

  /**
   * Touch Feedback for Mobile
   * Adds haptic-like visual feedback for touch interactions
   */
  function initTouchFeedback() {
    if (!('ontouchstart' in window)) return;

    const touchTargets = document.querySelectorAll(
      '.template-index .product-card, ' +
      '.template-index .collection-card, ' +
      '.template-index .gallery__item, ' +
      '.template-index .category-quick-nav__pill'
    );

    touchTargets.forEach(target => {
      target.addEventListener('touchstart', function() {
        this.classList.add('is-touched');
      }, { passive: true });

      target.addEventListener('touchend', function() {
        this.classList.remove('is-touched');
      }, { passive: true });

      target.addEventListener('touchcancel', function() {
        this.classList.remove('is-touched');
      }, { passive: true });
    });
  }

  /**
   * Header Interaction with Trust Bar
   * Coordinates trust bar visibility with header scroll behavior
   */
  function initHeaderTrustBarCoordination() {
    const header = document.querySelector('.header');
    const trustBar = document.getElementById('trust-bar-floating');

    if (!header || !trustBar) return;

    // Adjust trust bar position based on header height when visible
    function updateTrustBarPosition() {
      if (trustBar.classList.contains('is-visible')) {
        const headerHeight = header.offsetHeight;
        if (header.classList.contains('is-sticky') || header.classList.contains('header-sticky')) {
          trustBar.style.top = `${headerHeight}px`;
        } else {
          trustBar.style.top = '0';
        }
      }
    }

    // Observe header class changes
    const headerObserver = new MutationObserver(updateTrustBarPosition);
    headerObserver.observe(header, { attributes: true, attributeFilter: ['class'] });

    // Also update on scroll
    window.addEventListener('scroll', updateTrustBarPosition, { passive: true });
  }

  /**
   * Initialize All Features
   */
  function init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initAll);
    } else {
      initAll();
    }
  }

  function initAll() {
    initSectionReveals();
    initProductCardStagger();
    initSmoothScroll();
    initParallax();
    initImageFadeIn();
    initTouchFeedback();
    initHeaderTrustBarCoordination();
  }

  // Start initialization
  init();

})();
